import { Inject } from '@odinjs/odin';
import { ButtonAPI, DeckAPI, DeckValue, PanelAPI, TextAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { FragmentNames, PhysicianScheduleBlockIndividualAliases, VitalSignPainScaleFieldAliases } from '../../../codegen';

import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import BaseController from '../../BaseController';
import { SimpleDialog } from '../../dialog-helper';

enum ScheduleStatus {
  AVAILABLE = 1,
  BLOCKED = 50,
}

interface ScheduleDetail {
  personEmail?: string;
  personName?: string;
  personPhoneNumber?: string;
  personPhoto?: string;
  personPhotoBase64?: string;
  personSpeciality?: string;
  scheduleCode?: number;
  scheduleDate?: string;
  scheduleDateTime?: string;
  scheduleEndTime?: string;
  scheduleSequence?: number;
  scheduleStartTime?: string;
  scheduleStatusCode?: number;
  scheduleStatusDescription?: string;
  scheduleType?: string;
  scheduleTypeDescription?: string;
}

@Controller({ component: PhysicianScheduleBlockIndividualAliases.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL_LAYOUT, fragment: FragmentNames.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL })
class PhysicianScheduleBlockIndividualController extends BaseController {

  @Inject(PhysicianScheduleBlockIndividualAliases.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL_DECK)
  physicanScheduleDeckApi!: DeckAPI;

  @Inject(PhysicianScheduleBlockIndividualAliases.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL_TITLE)
  scheduleBlockTitleApi!: TextAPI;

  @Inject(PhysicianScheduleBlockIndividualAliases.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL_BUTTON)
  blockButtonApi!: ButtonAPI;

  @Inject(PhysicianScheduleBlockIndividualAliases.PHYSICIAN_SCHEDULE_UNLOCK_INDIVIDUAL_BUTTON)
  unlockButtonApi!: ButtonAPI;

  @Inject(VitalSignPainScaleFieldAliases.PAIN_SCALE_FIELD_PANEL_MODAL)
  panelLoaderModalApi!: PanelAPI;

  @OnMount({ target: PhysicianScheduleBlockIndividualAliases.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL_DECK })
  handleMountDeck(): void {
    const { establishmentCode, scheduleSequence, scheduleType } = this.historyService.getCurrentRouteParams();
    this.physicanScheduleDeckApi.setLoading(true);
    HttpService.get<ScheduleDetail>(Endpoint.GET_PHYSICIAN_SCHEDULE_DETAIL, { establishmentCode, scheduleSequence, scheduleType })
      .then((response: HttpResponse<ScheduleDetail>) => {
        const scheduleBlockIndividual = response.body as ScheduleDetail;
        const descBlockTitle = `${moment(String(scheduleBlockIndividual.scheduleDate)).utc().format('DD MMMM')} ${scheduleBlockIndividual.scheduleStartTime} - ${scheduleBlockIndividual.scheduleEndTime}`;

        this.physicanScheduleDeckApi.setData([scheduleBlockIndividual as DeckValue]);
        this.scheduleBlockTitleApi.setValue(descBlockTitle);

        scheduleBlockIndividual.scheduleDateTime = this.scheduleBlockTitleApi.getValue();

        this.context.set({ scheduleBlockIndividual });

        if (scheduleBlockIndividual.scheduleStatusCode === ScheduleStatus.AVAILABLE) {
          this.blockButtonApi.setVisible(true);
        }
        if (scheduleBlockIndividual.scheduleStatusCode === ScheduleStatus.BLOCKED) {
          this.unlockButtonApi.setVisible(true);
        }
      }).finally(() => this.physicanScheduleDeckApi.setLoading(false));
  }

  @OnClick({ target: PhysicianScheduleBlockIndividualAliases.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL_BUTTON })
  handleClickBlockButton(): void {
    const { scheduleCode, scheduleDateTime, scheduleSequence } = this.context.get('scheduleBlockIndividual');
    const { establishmentCode } = this.historyService.getCurrentRouteParams();

    applicationContext.redirect(ApplicationRoute.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL_EDIT, { establishmentCode, scheduleCode, scheduleDateTime, scheduleSequence });
  }

  @OnClick({ target: PhysicianScheduleBlockIndividualAliases.PHYSICIAN_SCHEDULE_UNLOCK_INDIVIDUAL_BUTTON })
  handleClickUnlockButton(): void {
    this.handleScheduleunblock();
  }

  handleScheduleunblock(): void {
    const { scheduleCode, scheduleSequence } = this.context.get('scheduleBlockIndividual');
    const { establishmentCode } = this.historyService.getCurrentRouteParams();

    const body = {
      dayScheduleType: 'N',
      establishmentCode: establishmentCode,
      reasonCode: '',
      reasonDescription: '',
      scheduleCode,
      scheduleSequence,
      shapeConfSequence: '',
      statusType: 'B',
      userName: '',
    } as Record<string, Value>;

    HttpService.post(Endpoint.POST_PHYSICIAN_SCHEDULE_BLOCK_UNBLOCK_INDIVIDUAL, {}, { body })
      .catch((error) => SimpleDialog.showErrorMessage(error))
      .finally(() => applicationContext.popNavigationItem())
  }

}

export default PhysicianScheduleBlockIndividualController;
